import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MailWarning, Loader2 } from "lucide-react";

// Discreet, persistent banner for logged-in users whose email is not verified.
// Non-blocking: it never prevents access to any page. Resend relies on the existing
// POST /api/auth/resend-verification. Never renders/logs any token.
export function EmailVerifyBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | limited | error

  if (!user || user.email_verified) return null;

  const resend = async () => {
    setStatus("sending");
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      setStatus("sent");
    } catch (e) {
      setStatus(e?.response?.status === 429 ? "limited" : "error");
    }
  };

  return (
    <div data-testid="email-verify-banner"
      className="mb-4 flex flex-col gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
        <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t("ev.banner.text")}</span>
      </div>
      <div className="flex items-center gap-3">
        {status === "sent" && <span className="text-emerald-600 dark:text-emerald-400" data-testid="banner-resend-sent">{t("ev.banner.sent")}</span>}
        {status === "limited" && <span className="text-amber-600 dark:text-amber-400" data-testid="banner-resend-limited">{t("ev.banner.limited")}</span>}
        {status === "error" && <span className="text-destructive" data-testid="banner-resend-error">{t("ev.banner.error")}</span>}
        <button onClick={resend} disabled={status === "sending"} data-testid="banner-resend-btn"
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-amber-500 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-600 disabled:opacity-50">
          {status === "sending" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {status === "sending" ? t("ev.banner.sending") : t("ev.banner.resend")}
        </button>
      </div>
    </div>
  );
}
